/**
 * 档案库
 *
 * 归档的目标是「多年以后还能还原当时发生了什么」，所以检索入口按财务/审计最常用的
 * 四条线索组织：内部单号、发票号码、销方名称、**记账凭证号**（审计通常从凭证倒查发票）。
 *
 * 页面上刻意区分两种导出：
 *   - 「导出当前页」（前端 CSV，快捷，不留痕）；
 *   - 「申请审计导出」（后端 /archives/export，生成文件并留痕，用于对外提供资料）。
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ExportOutlined, FileSearchOutlined, InboxOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  INVOICE_TYPE_LABELS,
  PermissionCode,
  type ArchiveListItem,
} from '@ilc/shared';
import { archiveApi } from '@/api/endpoints';
import { mockArchiveList } from '@/mocks';
import { usePagedList } from '@/hooks/usePagedList';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { RiskTag } from '@/components/RiskTag';
import { Money } from '@/components/Money';
import { exportCsv } from '@/utils/export';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';

interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  sellerName?: string;
  contractNo?: string;
  booked?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

export default function Archive(): React.ReactElement {
  const navigate = useNavigate();
  const can = useAuthStore((s) => s.can);
  const canExport = can(PermissionCode.ARCHIVE_EXPORT);
  const [detail, setDetail] = useState<ArchiveListItem | null>(null);
  const [exporting, setExporting] = useState(false);

  const list = usePagedList<ArchiveListItem, Query>({
    // 档案接口的查询条件是开放结构（PageQuery & Record<string, unknown>），
    // 这里展开一次把本页的强类型 Query 转过去。
    loader: (q) => archiveApi.list({ ...q }),
    fallback: (q) => mockArchiveList(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const rows = list.list;
  const stats = {
    total: list.total,
    booked: rows.filter((r) => r.voucherNo !== null).length,
    withContract: rows.filter((r) => r.contractNo !== null).length,
    highRisk: rows.filter((r) => r.riskLevel === 'HIGH').length,
  };

  const handleAuditExport = async (): Promise<void> => {
    setExporting(true);
    const result = await performAction({
      action: () => archiveApi.export({ ...list.query }),
      successMessage: '审计导出任务已提交，生成后会通过站内消息通知（导出行为会记入操作日志）',
      offline: () => undefined,
      offlineMessage: '后端审计导出接口尚未就绪，本次未生成导出文件；可先用「导出当前页」做临时核对',
    });
    setExporting(false);
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="档案库"
        description="按供应商、合同、记账凭证号检索历史发票及其完整校验记录。归档快照保证多年后可还原当时的票面与校验结果。"
        extra={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={() =>
                exportCsv(
                  '档案检索结果',
                  [
                    { title: '内部单号', dataIndex: 'bizNo' },
                    { title: '发票代码', dataIndex: 'invoiceCode' },
                    { title: '发票号码', dataIndex: 'invoiceNo' },
                    { title: '销方名称', dataIndex: 'sellerName' },
                    { title: '价税合计(元)', value: (r) => (r.totalCents ?? 0) / 100 },
                    { title: '开票日期', dataIndex: 'invoiceDate' },
                    { title: '合同编号', dataIndex: 'contractNo' },
                    { title: '凭证号', dataIndex: 'voucherNo' },
                    { title: '校验报告号', dataIndex: 'reportNo' },
                    { title: '归档时间', dataIndex: 'archivedAt' },
                  ],
                  rows,
                )
              }
            >
              导出当前页
            </Button>
            <Button
              type="primary"
              icon={<InboxOutlined />}
              disabled={!canExport}
              loading={exporting}
              onClick={() => void handleAuditExport()}
            >
              申请审计导出
            </Button>
          </Space>
        }
      />

      {!canExport ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="审计导出需要 archive:export 权限"
          description="您可以检索与查看档案，但不能导出对外资料。导出会生成带水印与操作人信息的文件并留痕，如需权限请联系租户管理员。"
        />
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="档案总数" value={stats.total} suffix="张" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="本页已有凭证号" value={stats.booked} suffix="张" valueStyle={{ color: '#389e0d' }} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="本页已关联合同" value={stats.withContract} suffix="张" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="本页高风险归档"
              value={stats.highRisk}
              suffix="张"
              valueStyle={{ color: stats.highRisk > 0 ? '#cf1322' : undefined }}
              prefix={stats.highRisk > 0 ? <WarningOutlined /> : undefined}
            />
          </Card>
        </Col>
      </Row>

      <DataTable<ArchiveListItem>
        storageKey="archive-list"
        exportFileName="档案库"
        exportColumns={[
          { title: '内部单号', dataIndex: 'bizNo' },
          { title: '发票号码', dataIndex: 'invoiceNo' },
          { title: '销方名称', dataIndex: 'sellerName' },
          { title: '合同编号', dataIndex: 'contractNo' },
          { title: '凭证号', dataIndex: 'voucherNo' },
          { title: '归档时间', dataIndex: 'archivedAt' },
        ]}
        columns={[
          {
            title: '内部单号',
            dataIndex: 'bizNo',
            width: 160,
            fixed: 'left',
            render: (v: string, record) => (
              <a className="ilc-mono" style={{ fontSize: 12 }} onClick={() => navigate(`/business/invoices/${record.invoiceId}`)}>
                {v}
              </a>
            ),
          },
          {
            title: '发票号码',
            dataIndex: 'invoiceNo',
            width: 180,
            render: (v: string, record) => (
              <Space direction="vertical" size={0}>
                <span className="ilc-mono" style={{ fontSize: 12 }}>
                  {v}
                </span>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {INVOICE_TYPE_LABELS[record.invoiceType] ?? record.invoiceType}
                </Typography.Text>
              </Space>
            ),
          },
          { title: '销方名称', dataIndex: 'sellerName', width: 200, ellipsis: true, render: (v: string | null) => v ?? '-' },
          {
            title: '价税合计',
            dataIndex: 'totalCents',
            width: 140,
            align: 'right',
            render: (v: number | null) => <Money cents={v} strong />,
          },
          {
            title: '开票日期',
            dataIndex: 'invoiceDate',
            width: 115,
            render: (v: string | null) =>
              v ? <span className="ilc-mono">{`${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`}</span> : '-',
          },
          {
            title: '合同编号',
            dataIndex: 'contractNo',
            width: 150,
            render: (v: string | null) =>
              v ? (
                <span className="ilc-mono" style={{ fontSize: 12 }}>
                  {v}
                </span>
              ) : (
                <Tag>未关联</Tag>
              ),
          },
          {
            title: '记账凭证号',
            dataIndex: 'voucherNo',
            width: 170,
            render: (v: string | null) =>
              v ? (
                <span className="ilc-mono" style={{ fontSize: 12 }}>
                  {v}
                </span>
              ) : (
                <Tag color="orange">待 T+ 端生成</Tag>
              ),
          },
          {
            title: '校验报告号',
            dataIndex: 'reportNo',
            width: 170,
            render: (v: string | null, record) =>
              v ? (
                <a className="ilc-mono" style={{ fontSize: 12 }} onClick={() => navigate(`/business/invoices/${record.invoiceId}/report`)}>
                  {v}
                </a>
              ) : (
                '-'
              ),
          },
          {
            title: '风险',
            dataIndex: 'riskLevel',
            width: 110,
            render: (v: ArchiveListItem['riskLevel']) => <RiskTag level={v} />,
          },
          {
            title: '归档时间',
            dataIndex: 'archivedAt',
            width: 150,
            render: (v: string) => <span className="ilc-mono" style={{ fontSize: 12 }}>{v.replace('T', ' ').slice(0, 16)}</span>,
          },
          {
            title: '操作',
            key: 'action',
            width: 140,
            fixed: 'right',
            render: (_v, record) => (
              <Space size={4}>
                <Button size="small" type="link" onClick={() => setDetail(record)}>
                  归档详情
                </Button>
              </Space>
            ),
          },
        ]}
        dataSource={rows}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        emptyText="没有匹配的归档记录，试试换用凭证号或销方名称检索"
        toolbar={
          <Space wrap>
            <Input.Search
              placeholder="内部单号 / 发票号码 / 销方 / 凭证号"
              allowClear
              style={{ width: 280 }}
              prefix={<FileSearchOutlined />}
              onSearch={(value) => list.setQuery({ keyword: value })}
            />
            <Input.Search
              placeholder="销方名称"
              allowClear
              style={{ width: 200 }}
              onSearch={(value) => list.setQuery({ sellerName: value })}
            />
            <Input.Search
              placeholder="合同编号"
              allowClear
              style={{ width: 180 }}
              onSearch={(value) => list.setQuery({ contractNo: value })}
            />
            <Space size={4}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                只看已有凭证号
              </Typography.Text>
              <Switch
                size="small"
                checked={list.query.booked === true}
                onChange={(checked) => list.setQuery({ booked: checked ? true : undefined })}
              />
            </Space>
            <Select
              placeholder="归档时间范围"
              style={{ width: 180 }}
              allowClear
              value={
                list.query.dateFrom && list.query.dateTo
                  ? `${list.query.dateFrom}-${list.query.dateTo}`
                  : undefined
              }
              onChange={(value: string | undefined) => {
                if (!value) {
                  list.setQuery({ dateFrom: undefined, dateTo: undefined });
                  return;
                }
                const [from, to] = value.split('-');
                list.setQuery({ dateFrom: from, dateTo: to });
              }}
              options={[
                { value: `${dayjs().subtract(30, 'day').format('YYYYMMDD')}-${dayjs().format('YYYYMMDD')}`, label: '近 30 天' },
                { value: `${dayjs().subtract(90, 'day').format('YYYYMMDD')}-${dayjs().format('YYYYMMDD')}`, label: '近 90 天' },
                { value: `${dayjs().subtract(1, 'year').format('YYYYMMDD')}-${dayjs().format('YYYYMMDD')}`, label: '近一年' },
              ]}
            />
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      <Modal
        open={detail !== null}
        title={`归档详情 —— ${detail?.bizNo ?? ''}`}
        onCancel={() => setDetail(null)}
        width={680}
        footer={
          <Space>
            <Button onClick={() => setDetail(null)}>关闭</Button>
            <Button
              type="primary"
              onClick={() => navigate(`/business/invoices/${detail?.invoiceId ?? ''}/report`)}
            >
              查看完整校验报告
            </Button>
          </Space>
        }
      >
        {detail ? (
          <>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="内部单号">
                <span className="ilc-mono">{detail.bizNo}</span>
              </Descriptions.Item>
              <Descriptions.Item label="发票号码">
                <span className="ilc-mono">{detail.invoiceNo}</span>
              </Descriptions.Item>
              <Descriptions.Item label="发票代码">
                <span className="ilc-mono">{detail.invoiceCode ?? '（数电票无代码）'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="票种">
                {INVOICE_TYPE_LABELS[detail.invoiceType] ?? detail.invoiceType}
              </Descriptions.Item>
              <Descriptions.Item label="销方名称">{detail.sellerName ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="价税合计">
                <Money cents={detail.totalCents} strong withChinese />
              </Descriptions.Item>
              <Descriptions.Item label="开票日期">
                <span className="ilc-mono">{detail.invoiceDate ?? '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="归档时间">
                <span className="ilc-mono">{detail.archivedAt.replace('T', ' ').slice(0, 19)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="关联合同">
                {detail.contractNo ? <span className="ilc-mono">{detail.contractNo}</span> : <Tag>未关联</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="记账凭证号">
                {detail.voucherNo ? (
                  <span className="ilc-mono">{detail.voucherNo}</span>
                ) : (
                  <Tag color="orange">待 T+ 端生成</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="校验报告号">
                <span className="ilc-mono">{detail.reportNo ?? '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="归档时风险等级">
                <RiskTag level={detail.riskLevel} />
              </Descriptions.Item>
            </Descriptions>

            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="归档快照"
              description="归档时会保存发票、校验结果、风险项与人工操作记录的完整快照。凭证号由 T+ 回写后补入档案，因此归档时可能为空——这不影响档案的完整性。"
            />

            <Card size="small" title="档案可追溯内容" style={{ marginTop: 12 }}>
              <Table
                size="small"
                rowKey="item"
                pagination={false}
                dataSource={[
                  { item: '发票原件', note: '原件文件与内容哈希，重复上传自动去重' },
                  { item: 'OCR 识别结果', note: '含逐字段置信度与人工修正记录（修改前/后/原因/操作人）' },
                  { item: '外部校验原始报文', note: '企查查、税务局验真的原始返回，供审计核对' },
                  { item: '风险项清单', note: '风险等级、触发证据与处置建议' },
                  { item: '人工接管说明', note: '外部接口不可用时的核验说明与核验项' },
                  { item: '审批与特例放行记录', note: '初审意见、退回原因、专项审批依据' },
                  { item: 'T+ 推送与回写', note: '推送报文、T+ 单据号、凭证号回写报文' },
                ]}
                columns={[
                  { title: '内容', dataIndex: 'item', width: 200 },
                  { title: '说明', dataIndex: 'note' },
                ]}
              />
            </Card>
          </>
        ) : null}
      </Modal>

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
        提示：档案检索同样受数据权限约束（本人 / 本部门 / 本租户），跨部门档案需要相应数据权限；
        所有导出行为（包含前端 CSV）都会在使用须知中告知使用者，正式对外提供资料请走「申请审计导出」。
      </Typography.Paragraph>
    </div>
  );
}
